import type {
    ComposableOptionsBase,
    Sku,
    SkusMap,
    ProcessSkusOutput,
    ProductAttrsMap,
    ProductAttrsGroupOption,
    ProductOptionsModel,
    ProductAttrsSkuOption,
    ProductOrderedOptionsModel
} from '~/types';

import { findKey as lo_findKey } from 'es-toolkit';
import { xor as lo_xor } from 'es-toolkit';
import { trim as lo_trim } from 'es-toolkit';
import { isEqual as lo_isEqual } from 'es-toolkit';
import { createSharedComposable } from '@vueuse/core';


export interface UseSkusUtilsOptions extends ComposableOptionsBase {}
export function useSkusUtilsStandalone(options: Partial<UseSkusUtilsOptions> = {}) {

    function processSkus(
        skus: Sku[]
    ): ProcessSkusOutput {
        const skusMap: SkusMap = {};
        let hasBundleTemplates = false;

        skus.forEach((sku: Sku) => {
            if (sku.optionUids) {
                skusMap[sku.uid] = sku.optionUids.slice(0);
            }

            if (sku.bundleTemplate) {
                hasBundleTemplates = true;
            }
        });

        return {
            skusMap,
            hasBundleTemplates
        };
    }

    function getSkusMap(
        skus: Sku[]
    ): SkusMap {
        return processSkus(skus).skusMap;
    }

    function getSkuByUid(
        skus: Sku[],
        uid: string
    ): Nullable<Sku> {
        return skus.find((sku) => sku.uid === uid) ?? null;
    }

    function getSkuBySlug(
        skus: Sku[],
        slug: string
    ): Nullable<Sku> {
        slug = lo_trim(slug, '/');
        return skus.find((sku) => sku.slug === slug) ?? null;
    }

    function getSkuBySelectedOptions(
        skus: Sku[],
        skusMap: SkusMap,
        opts: ProductOptionsModel
    ): Nullable<Sku> {
        const attrsModel: string[] = Object.values(opts);
        const uid = lo_findKey(skusMap, (attrs: string[]) => {
            const x = lo_xor(attrsModel, attrs);
            return !x.length;
        });

        if (!uid) {
            return null;
        }

        return getSkuByUid(skus, uid);
    }

    function getSkuBySelectedOrderedOptions(
        skus: Sku[],
        opts: ProductOrderedOptionsModel
    ): Nullable<Sku> {
        return skus.find(
            (sku) => lo_isEqual(sku.optionUids, opts)
        ) ?? null;
    }

    function getValuesModelBySku(
        skusMap: SkusMap,
        attrsMap: ProductAttrsMap,
        skuUid: string
    ): Record<string, string> {
        const model: Record<string, string> = {};
        const attVals = skusMap[skuUid];

        attVals?.forEach((attrVal) => {
            const attr = lo_findKey<Record<string, string[]>>(
                attrsMap,
                (attr) => attr.includes(attrVal)
            );

            if (attr) {
                model[attr] = attrVal;
            }
        });

        return model;
    }

    function findBestSkusBySelectedOptions(
        skusMap: SkusMap,
        attrsMap: ProductAttrsMap,
        opts: Record<string, string>,
        attrValFilter?: string
    ): Nullable<string> {
        const attrsModel: string[] = Object.values(opts);
        const allPossibleAttrs = Object.keys(attrsMap);
        let bestSku: Nullable<string> = null;
        let bestCount = allPossibleAttrs.length;

        for (const [k, attrs] of Object.entries(skusMap)) {
            if (attrValFilter && !attrs.includes(attrValFilter)) {
                continue;
            }
            
            const x = lo_xor(attrsModel, attrs);
            const l = x.length;

            if (l <= bestCount) {
                bestSku = k;
                bestCount = l;
            }
        }

        return bestSku;
    }

    function findFirstSkuByPartialOptions(
        skus: Sku[],
        model: ProductOptionsModel
    ): Nullable<Sku> {
        const opts = Object.values(model);
        return skus.find(
            (sku) => {
                return opts.every(
                    (opt: string) => sku.optionUids?.includes(opt)
                );
            }
        ) || null;
    }

    function getColorOptFromSkuOptUids(
        optionGroups: ProductAttrsGroupOption[],
        optionUids: string[]
    ): Undefinable<ProductAttrsSkuOption> {
        const colorOpt = optionGroups.find((opt) => opt.attribute.isColor);

        if (colorOpt && optionUids) {
            return colorOpt.groupValues.find(
                (attr) => optionUids.includes(attr.value.uid)
            );
        }
    }

    function checkForBundleGroups(skus: Sku[]): boolean {
        return !!skus.find((o) => !!o.bundleTemplate);
    }

    return {
        processSkus,
        getSkusMap,
        getSkuByUid,
        getSkuBySlug,
        getSkuBySelectedOptions,
        getSkuBySelectedOrderedOptions,
        findBestSkusBySelectedOptions,
        findFirstSkuByPartialOptions,
        getColorOptFromSkuOptUids,
        getValuesModelBySku,
        checkForBundleGroups
    };
}

export const useSkusUtils = createSharedComposable(useSkusUtilsStandalone);
